import { Routes } from '@angular/router';
import { AutoLoginByConfigIdGuard } from './core/guards/auto-login-by-config-id.guard';
import { launchContextGuard } from './core/guards/launch-context.guard';
import { loadDceGuard } from './core/guards/load-dce.guard';
import { localOnlyMatchGuard } from './core/guards/local-only-match.guard';
import { roleBasedGuard } from './core/guards/role-based.guard';
import { waOspiTypeGuard } from './core/guards/wa-ospi-type.guard';
import { Constants } from './app.constants';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'educator'
  },
  {
    path: 'educator',
    loadComponent: () =>
      import('./features/shell/shell-layout.component').then((m) => m.ShellLayoutComponent),
    children: [
      {
        // Local dev only: restores the Home tiles page for bare /educator, which the
        // Access Denied route below serves in every other environment.
        path: '',
        pathMatch: 'full',
        canMatch: [localOnlyMatchGuard],
        canActivate: [AutoLoginByConfigIdGuard],
        title: 'Reports',
        loadComponent: () =>
          import('./features/home/home.component').then((m) => m.HomeComponent)
      },
      {
        // launchContextGuard still denies a cold bookmarked hit; AutoLoginByConfigIdGuard re-logs-in when launch context survived a logout.
        path: '',
        pathMatch: 'full',
        canActivate: [launchContextGuard, AutoLoginByConfigIdGuard],
        title: 'Access Denied',
        loadComponent: () =>
          import('./features/auth/unauthorized.component').then((m) => m.UnauthorizedComponent)
      },
      {
        path: 'reports',
        canActivate: [AutoLoginByConfigIdGuard],
        title: 'Reports',
        loadComponent: () =>
          import('./features/reports/reports.component').then((m) => m.ReportsComponent)
      },
      {
        path: 'summary-dce',
        canActivate: [launchContextGuard, AutoLoginByConfigIdGuard, loadDceGuard],
        title: 'Summary Report',
        data: {
          dceKey: 'summaryReport',
          bundleUrl: '',
          elementTag: '',
          title: 'Summary Report'
        },
        loadComponent: () =>
          import('./features/reports/summary-dce-host.component').then(
            (m) => m.SummaryDceHostComponent
          )
      },
      {
        // Matcher so deep md-isr sub-routes resolve to the host on refresh.
        matcher: (segments) =>
          segments.length > 0 && segments[0].path === 'md-isr'
            ? { consumed: segments }
            : null,
        canActivate: [launchContextGuard, AutoLoginByConfigIdGuard, roleBasedGuard, loadDceGuard],
        // No static `title` here: this one route covers both the individual report AND
        // bulk-print (internal DCE sub-routes, not separate shell routes) - a static title
        // would be re-applied by Angular's router title strategy on every navigation,
        // fighting the dynamic title set in md-isr-host.component.ts. That component sets
        // the title itself based on the DCE's actual sub-route.
        data: {
          dceKey: 'marylandIsr',
          bundleUrl: '',
          elementTag: '',
          title: 'Maryland ISR Report',
          allowedRoles: Constants.MD_ISR_ALLOWED_ROLES,
          requiredLicense: Constants.MD_ISR_REQUIRED_LICENSE
        },
        loadComponent: () =>
          import('./features/reports/md-isr-host.component').then(
            (m) => m.MdIsrHostComponent
          )
      },
      {
        path: 'extracts-ui',
        canActivate: [launchContextGuard, AutoLoginByConfigIdGuard, waOspiTypeGuard, loadDceGuard],
        title: 'Data Extracts',
        data: {
          dceKey: 'extractsUi',
          bundleUrl: '',
          elementTag: '',
          title: 'Data Extracts'
        },
        loadComponent: () =>
          import('./features/reports/extracts-ui-host.component').then(
            (m) => m.ExtractsUiHostComponent
          )
      },
      {
        path: 'teacher-report',
        canActivate: [launchContextGuard, AutoLoginByConfigIdGuard, roleBasedGuard, loadDceGuard],
        title: 'Student Growth by Teacher Report',
        data: {
          dceKey: 'teacherReport',
          bundleUrl: '',
          elementTag: '',
          title: 'Student Growth by Teacher Report',
          allowedRoles: Constants.TEACHER_REPORT_ALLOWED_ROLES
        },
        loadComponent: () =>
          import('./features/reports/teacher-report-host.component').then(
            (m) => m.TeacherReportHostComponent
          )
      },
      {
        path: 'student-reading-proficiency-report',
        canActivate: [launchContextGuard, AutoLoginByConfigIdGuard, roleBasedGuard, loadDceGuard],
        title: 'Student Reading Proficiency Report',
        data: {
          dceKey: 'studentReadingProficiencyReport',
          bundleUrl: '',
          elementTag: '',
          title: 'Student Reading Proficiency Report',
          allowedRoles: Constants.STUDENT_READING_PROFICIENCY_REPORT_ALLOWED_ROLES
        },
        loadComponent: () =>
          import('./features/reports/student-reading-proficiency-report-host.component').then(
            (m) => m.StudentReadingProficiencyReportHostComponent
          )
      },
      {
        // No guard: Access Denied must be reachable without ever triggering a login attempt.
        path: 'unauthorized',
        title: 'Unauthorized',
        loadComponent: () =>
          import('./features/auth/unauthorized.component').then((m) => m.UnauthorizedComponent)
      }
    ]
  },
  {
    path: 'logout',
    loadComponent: () =>
      import('./features/auth/logout.component').then((m) => m.LogoutComponent)
  },
  {
    path: 'educator/callback',
    loadComponent: () =>
      import('./features/auth/signin-callback.component').then((m) => m.SigninCallbackComponent)
  },
  {
    path: 'educator/signin-callback',
    loadComponent: () =>
      import('./features/auth/signin-callback.component').then((m) => m.SigninCallbackComponent)
  },
  {
    path: 'error',
    loadComponent: () =>
      import('./features/auth/error.component').then((m) => m.ErrorComponent)
  },
  {
    path: 'educator/silent-renew',
    loadComponent: () =>
      import('./features/auth/silent-renew.component').then((m) => m.SilentRenewComponent)
  },
  {
    path: '**',
    redirectTo: 'educator'
  }
];
